"use client"

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import AuthLayout from '@/components/auth/AuthLayout'
import { useResetPassword } from '@/lib/api/hooks/auth'
import { showToast } from '@/lib/toast'
import { Button } from '@/components/ui/Button'

const NewPassword = () => {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const resetPasswordMutation = useResetPassword()

  // Get userId and otp from URL params or sessionStorage (for backward compatibility)
  const getUserId = () => {
    return searchParams.get('userId') || searchParams.get('userid') || sessionStorage.getItem('userId')
  }

  const getOtp = () => {
    return searchParams.get('otp') || sessionStorage.getItem('otp')
  }

  // Redirect if userId or otp is missing
  useEffect(() => {
    const userId = getUserId()
    const otp = getOtp()
    
    if (!userId || !otp) {
      showToast.error('Invalid reset password link')
      navigate('/forgot-password', { replace: true })
    }
  }, [navigate, searchParams])

  // Password validation checks
  const passwordChecks = {
    minLength: newPassword.length >= 8,
    hasUpperCase: /[A-Z]/.test(newPassword),
    hasLowerCase: /[a-z]/.test(newPassword),
    hasNumbers: /\d/.test(newPassword),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    passwordsMatch: newPassword === confirmPassword && confirmPassword.length > 0
  }

  const validatePassword = () => {
    return passwordChecks.minLength && 
           passwordChecks.hasUpperCase && 
           passwordChecks.hasLowerCase && 
           passwordChecks.hasNumbers && 
           passwordChecks.hasSpecialChar
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const userId = getUserId()
    const otp = getOtp()

    if (!userId || !otp) {
      setError('Invalid reset password link')
      navigate('/forgot-password', { replace: true })
      return
    }

    // Validation
    if (!validatePassword()) {
      setError('Password must be at least 8 characters and contain uppercase, lowercase, numbers, and special characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    try {
      await resetPasswordMutation.mutateAsync({
        userId,
        otp,
        password: newPassword
      })

      // Clear all user credentials and temporary data
      sessionStorage.removeItem('userId')
      sessionStorage.removeItem('userid')
      sessionStorage.removeItem('otp')
      sessionStorage.removeItem('pending_user_id')
      
      // Clear form fields
      setNewPassword('')
      setConfirmPassword('')
      setError('')
      
      // Clear URL parameters from current page
      window.history.replaceState({}, '', window.location.pathname)
      
      showToast.success('Password reset successfully')
      
      // Navigate to login and replace current history entry
      navigate('/login', { replace: true })
    } catch (error: any) {
      console.error('Error resetting password:', error)
      setError(error?.response?.data?.message || 'Failed to reset password. Please try again.')
    }
  }

  return (
    <AuthLayout>
  
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-satoshi">New Password</h2>
        <p className="text-gray-600 font-satoshi">Create a new password</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-satoshi">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2 font-satoshi">
            New Password
          </label>
          <div className="relative">
            <input
              key="newPassword-input"
              id="newPassword"
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
                setError('')
              }}
              placeholder="Enter new password"
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none transition-colors bg-white font-satoshi"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          
          {newPassword.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <div className="text-xs font-medium text-gray-700 mb-2 font-satoshi">Password Requirements:</div>
              <div className="space-y-1 text-xs font-satoshi">
                <div className={`flex items-center ${passwordChecks.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                  <span className="mr-2">{passwordChecks.minLength ? '✓' : '○'}</span>
                  At least 8 characters
                </div>
                <div className={`flex items-center ${passwordChecks.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`}>
                  <span className="mr-2">{passwordChecks.hasUpperCase ? '✓' : '○'}</span>
                  One uppercase letter
                </div>
                <div className={`flex items-center ${passwordChecks.hasLowerCase ? 'text-green-600' : 'text-gray-500'}`}>
                  <span className="mr-2">{passwordChecks.hasLowerCase ? '✓' : '○'}</span>
                  One lowercase letter
                </div>
                <div className={`flex items-center ${passwordChecks.hasNumbers ? 'text-green-600' : 'text-gray-500'}`}>
                  <span className="mr-2">{passwordChecks.hasNumbers ? '✓' : '○'}</span>
                  One number
                </div>
                <div className={`flex items-center ${passwordChecks.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`}>
                  <span className="mr-2">{passwordChecks.hasSpecialChar ? '✓' : '○'}</span>
                  One special character (!@#$%^&*(),.?":{}|&lt;&gt;)
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2 font-satoshi">
            Confirm Password
          </label>
          <div className="relative">
            <input
              key="confirmPassword-input"
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
                setError('')
              }}
              placeholder="Confirm new password"
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none transition-colors bg-white font-satoshi"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          
          {confirmPassword.length > 0 && (
            <div className="mt-2">
              <div className={`flex items-center text-xs font-satoshi ${passwordChecks.passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{passwordChecks.passwordsMatch ? '✓' : '○'}</span>
                {passwordChecks.passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
              </div>
            </div>
          )}
        </div>

        <Button
          type="submit"
          variant="primary"
          loading={resetPasswordMutation.isPending}
          disabled={!newPassword || !confirmPassword}
          className="w-full font-medium py-3 px-4 rounded-lg font-satoshi"
        >
          {resetPasswordMutation.isPending ? 'Processing...' : 'Proceed'}
        </Button>
      </form>
    </AuthLayout>
  )
}

export default NewPassword